import React, {useCallback, useState, useRef, useEffect} from "react";
import debounce from "lodash.debounce";
import Vimeo from '@u-wave/react-vimeo';
import { AnimatePresence, motion, usePresence } from "framer-motion";
import ProgressiveBackgroundImg from "../../components/ProgressiveBackgroundImg/ProgressiveBackgroundImg";  
import './project-video.css';
import { primaryInput } from "detect-it";


const ProjectVideo = ({videoLink, setVideoProgress, setTimestamp, setDurationTimestamp, volume, data, projectNumber, handleMainMouseEnter, handleMainMouseLeave, setIsVideoPlaying2, scroller, seekVideoLocation = 0, isSecondaryView, isFullscreen, dynamicSrcType, alternateScrollRef,  alternateScrollFunc, setIsVideoLoading2, handleSeekEnd, isVideoSeeking, handleVideoEnd}) => {
    const loadingWrapperVariants = {
        initial: {
            opacity: 0
        }, animate: {
            opacity: 1, 
            transition: {
                duration: 1,
                ease: "easeIn"
            }
        }, exit: {
            opacity: 0, 
            transition: {
                duration: 0.65,
                ease: "easeOut"
            }
        }
    }

    const [isPresent, safeToRemove] = usePresence();

    // Vimeo controls
    const thumbnailWrapperRef = useRef(null);
    const mainVideoRef = useRef(null);
    const playerRef = useRef(null);
    const [videoDur, setVideoDur] = useState(null);
    const [videoPlayingControl, setVideoPlayingControl] = useState(false);
    const [isVideoPlaying, setIsVideoPlaying] = useState(false);
    const [clickingBlocked, setClickingBlocked] = useState(false);
    const [videoTime, setVideoTime] = useState(0); // Actual video time from Vimeo
    const [cachedElapedTime, setCachedElapsedTime] = useState(0);
    const startTimeRef = useRef(0); // Time when the video started playing (in ms)
    const animationFrameRef = useRef(null); 
    const [videoDuration, setVideoDuration] = useState(-1);
    const [fullscreenRatio, setFullscreenRatio] = useState(null);
    const [isWaitingForVideo, setIsWaitingForVideo] = useState(false);
    const [dynamicSrc, setDynamicSrc] = useState(null);

    const debounceIsUserWaiting = useCallback(
        debounce(() => {
            setIsWaitingForVideo(true);
            setIsVideoLoading2(true)
          }, 100),
        []
    );

    useEffect(()=> {
        if(dynamicSrcType){
            let newSrc = data.thumbnails.desktop.replace("desktop", dynamicSrcType)
            setDynamicSrc(newSrc);
        }
    }, [dynamicSrcType])

    useEffect(() => {
        if(!isPresent){
            setIsVideoPlaying(false);
            safeToRemove()
        }
    }, [isPresent])

    useEffect(() => {
        let a;
        if(isWaitingForVideo){
            try{
                mainVideoRef.current.style.filter = "grayscale(1) invert(0) contrast(2)"
            } catch{}
        } else{
            try{
                mainVideoRef.current.style.filter = "grayscale(0) invert(0) contrast(1)"

            } catch{}
        }
    }, [isWaitingForVideo])

    const handleVideoClick = () => {
        if(!clickingBlocked){setVideoPlayingControl(!videoPlayingControl); setClickingBlocked(true);}
        
        if(!isVideoPlaying)debounceIsUserWaiting()

        if(!isVideoPlaying){
            if(primaryInput !== "touch"){
                scroller.scrollTo( 'top', {
                    'offset': 0,
                    'callback': function() {
                    },
                    'duration': 1000,
                    'easing': [0.25, 0.00, 0.35, 1.00],
                    'disableLerp': false
                } );
            } else{
                try{
                alternateScrollFunc(
                    alternateScrollRef.current,
                    0,
                    0,
                    500, // 500ms
                    [0.25, 0.0, 0.35, 1.0]
                )
                }catch{
                    console.warn("error trying to snap scroll")
                }
            }
        }
    }

    const handleTimeUpdate = (e) => {
        let seconds = e.seconds;
        let percent = e.percent;
        if(videoDuration == -1){setVideoDuration(e.duration)}
        if(!isVideoSeeking && isVideoPlaying) setVideoTime(e.seconds);
    }

    const convertTimestamp = (time) => {
        let minutes = Math.floor(time/60);
        let seconds = Math.floor(time - (minutes*60));
        let ms = (time % 1).toFixed(3) * 1000;
        if(ms  == 0) ms = '000';
        else if(ms < 10) ms = '00'+ms;
        else if(ms < 100) ms = '0'+ms;
        if(minutes < 10) minutes = '0'+minutes;
        if(seconds < 10) seconds = '0'+seconds;
        return (minutes+":"+seconds+"."+ms)
    }

    const updateTimer = () => {
        // Calculate the elapsed time by checking the difference between current time and start time
        const currentTime = Date.now();
        const timeElapsed = (currentTime - startTimeRef.current) / 1000; // Convert from ms to seconds

        // Update the state with the new elapsed time
        setVideoProgress((cachedElapedTime + timeElapsed)/videoDuration);
        setTimestamp(convertTimestamp(cachedElapedTime + timeElapsed));

        let curProg = (cachedElapedTime + timeElapsed)/videoDuration;
        if(curProg < 0.99){
            // Request the next animation frame if the video is still playing
            if (isVideoPlaying && !isVideoSeeking) {
                animationFrameRef.current = requestAnimationFrame(updateTimer);
            } else{
                setVideoProgress(videoTime/videoDuration);
            }
        } else{
            handleVideoEnd()
        }
    };

    useEffect(() => {
        if(isVideoSeeking){
            setIsWaitingForVideo(true);
        }
    }, [isVideoSeeking])

    const handleSeekingEnd = () => {
        setIsVideoPlaying(true);
        setIsWaitingForVideo(false);
        handleSeekEnd();
    }

    const handlePlayingStart = () => {
        debounceIsUserWaiting.cancel()
        setIsWaitingForVideo(false);
        setIsVideoLoading2(false);
        setIsVideoPlaying(true);
        setIsVideoPlaying2(true);
    }

    const handlePlayingStopped = () => {
        setIsVideoPlaying(false);
        setIsVideoPlaying2(false);
        setVideoPlayingControl(false);
    }   

    useEffect(() => {
        if(playerRef.current && seekVideoLocation != 0 && videoDuration > 0){

            playerRef.current.setCurrentTime(seekVideoLocation * videoDuration)
        }
    }, [seekVideoLocation])

    useEffect(() => {
        if(videoDuration > 0) setDurationTimestamp(convertTimestamp(videoDuration));
    }, [videoDuration])

    useEffect(() => {
        
        if(isVideoPlaying && videoDuration > 0 && !isVideoSeeking){
            startTimeRef.current = Date.now();
            animationFrameRef.current = requestAnimationFrame(updateTimer)
        } else{
            if(isVideoSeeking){
                setCachedElapsedTime(seekVideoLocation*videoDuration);
                setVideoProgress(seekVideoLocation);
            }else {
                setCachedElapsedTime(videoTime);
                setVideoProgress(videoTime/videoDuration);
            }
            cancelAnimationFrame(animationFrameRef.current);
        }
    }, [isVideoPlaying, videoDuration, isVideoSeeking]);

    useEffect(() => {
        if(isVideoPlaying){if(thumbnailWrapperRef.current){thumbnailWrapperRef.current.style.opacity = 0}}
    }, [isVideoPlaying])

    useEffect(() => {
        if(clickingBlocked){
            setTimeout(() => {
                setClickingBlocked(false)
            }, 200)
        }
    }, [clickingBlocked])

    useEffect(() => {
        let fullWidth = window.innerWidth;
        let fullHeight = window.innerHeight;
        if(mainVideoRef.current){
            let vidAspectRatio = data.aspectRatio;
            let bounding = mainVideoRef.current.getBoundingClientRect();
            let mvWidth = bounding.height * vidAspectRatio;
            let maxWidthScaler = fullWidth/mvWidth;
            let maxHeightScaler = fullHeight/bounding.height;
            

            let useScaler = Math.min(maxWidthScaler, maxHeightScaler);

            setFullscreenRatio(useScaler)
        }
    }, [])

    useEffect(() => {
        if(mainVideoRef.current){
            let targ = mainVideoRef.current;
            if(isFullscreen == true){
                targ.style.transitionDelay = '0.25s';
                if(window.innerHeight / window.innerWidth > 1){
                    targ.style.transitionDuration = '1s';
                }
                targ.style.transform = "translate(0, calc(50dvh - 60px - 50%)) scale("+fullscreenRatio+")";
                
                targ.style.zIndex = 5;
            }else if (isFullscreen == false){
                targ.style.transitionDelay = '0s';
                if(window.innerHeight / window.innerWidth > 1){
                    targ.style.transitionDuration = '1s';
                }
                targ.style.transform = "translate(0, 0) scale(1)";
                targ.style.zIndex = null;
            }
        }
    }, [isFullscreen])

    const mainVideoVariants = {
        initial: {
            transform: "translate(0, calc(50vh - 60px - 50%)) scale("+fullscreenRatio+")",
            zIndex: null  
        },
        animate: (i) => ({
            transform: i ? "translate(0, calc(50vh - 60px - 50%)) scale("+fullscreenRatio+")" : "translate(0, 0) scale(1)",
            zIndex: i ? null : 5,
            transition: {
                delay: 0.2,
                duration: 1.25,
                ease: [0.87, 0, 0.13, 1]
            }
        }),
        exit: {
            transform: "translate(0, 0) scale(1)",
            zIndex: null,
            transition: {
                duration: 0.5, // Add a duration for the exit
                ease: [0.87, 0, 0.13, 1]
            }
        }
    }

    const videoVariants = {
        initial: {
            opacity: 1
        }, animate: {
            opacity: 1
        }, exit: {
            opacity: 0,
            transition: {
                duration: 1,
                ease: [0.16, 1, 0.3, 1]
            }
        }
    }

    return(
        <motion.div
            className="main_video__content"
            onMouseEnter={handleMainMouseEnter}
            onMouseLeave={handleMainMouseLeave}
            style={{
                height: isSecondaryView ? '100%' : null,
            }}
            ref={mainVideoRef}

            initial="initial"
            animate="animate"
            exit="exit"
            variants={videoVariants}
        >
            <AnimatePresence mode="wait">
            {
                isWaitingForVideo &&
                <motion.div 
                    className="vid_loader__wrapper"
                    initial="initial"    
                    animate="animate"
                    exit="exit"
                    variants={loadingWrapperVariants}
                >
                    <span 
                        id="ghost"
                    style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%) scale(1, 1)",
                        transformOrigin: "center",
                        color: "var(--light-color)",
                        filter: "blur(0.5px)"

                    }}>LOADING</span>
                    <div className="vid_loader">
                        <div className="video-loader-char">
                            <span style={{opacity: 0}}>L</span>
                            <div className="v-ticker-1">
                                <span>L</span>
                                <span>L</span>
                                <span>L</span>
                                <span>L</span>
                                <span>L</span>
                                <span>L</span>
                                <span>L</span>
                            </div>
                            <div className="v-ticker-2">
                                <span>L</span>
                                <span>L</span>
                                <span>L</span>
                                <span>L</span>
                                <span>L</span>
                                <span>L</span>
                                <span>L</span>
                            </div>
                        </div>
                        <div className="video-loader-char">
                            <span style={{opacity: 0}}>O</span>
                            <div className="v-ticker-1"
                                style={{animationDuration: "20s"}}
                            >
                                <span>O</span>
                                <span>O</span>
                                <span>O</span>
                                <span>O</span>
                                <span>O</span>
                                <span>O</span>
                                <span>O</span>
                            </div>
                            <div className="v-ticker-2" style={{animationDuration: "20s"}}>
                                <span>O</span>
                                <span>O</span>
                                <span>O</span>
                                <span>O</span>
                                <span>O</span>
                                <span>O</span>
                                <span>O</span>
                            </div>
                        </div>
                        <div className="video-loader-char">
                            <span style={{opacity: 0}}>A</span>
                            <div className="v-ticker-1"
                                style={{animationDuration: "10s"}}
                            >
                                <span>A</span>
                                <span>A</span>
                                <span>A</span>
                                <span>A</span>
                                <span>A</span>
                                <span>A</span>
                                <span>A</span>
                            </div>
                            <div className="v-ticker-2" style={{animationDuration: "10s"}}>
                                <span>A</span>
                                <span>A</span>
                                <span>A</span>
                                <span>A</span>
                                <span>A</span>
                                <span>A</span>
                                <span>A</span>
                            </div>
                        </div>
                        <div className="video-loader-char">
                            <span style={{opacity: 0}}>D</span>
                            <div className="v-ticker-1"
                                style={{animationDuration: "30s"}}
                            >
                                <span>D</span>
                                <span>D</span>
                                <span>D</span>
                                <span>D</span>
                                <span>D</span>
                                <span>D</span>
                                <span>D</span>
                            </div>
                            <div className="v-ticker-2" style={{animationDuration: "30s"}}>
                                <span>D</span>
                                <span>D</span>
                                <span>D</span>
                                <span>D</span>
                                <span>D</span>
                                <span>D</span>
                                <span>D</span>
                            </div>
                        </div>
                        <div className="video-loader-char">
                            <span style={{opacity: 0}}>I</span>
                            <div className="v-ticker-1"
                                style={{animationDuration: "30s"}}
                            >
                                <span>I</span>
                                <span>I</span>
                                <span>I</span>
                                <span>I</span>
                                <span>I</span>
                                <span>I</span>
                                <span>I</span>
                            </div>
                            <div className="v-ticker-2" style={{animationDuration: "30s"}}>
                                 <span>I</span>
                                <span>I</span>
                                <span>I</span>
                                <span>I</span>
                                <span>I</span>
                                <span>I</span>
                                <span>I</span>
                            </div>
                        </div>
                        <div className="video-loader-char">
                            <span style={{opacity: 0}}>N</span>
                            <div className="v-ticker-1"
                                style={{animationDuration: "22s"}}
                            >
                                <span>N</span>
                                <span>N</span>
                                <span>N</span>
                                <span>N</span>
                                <span>N</span>
                                <span>N</span>
                                <span>N</span>
                            </div>
                            <div className="v-ticker-2" style={{animationDuration: "22s"}}>
                                <span>N</span>
                                <span>N</span>
                                <span>N</span>
                                <span>N</span>
                                <span>N</span>
                                <span>N</span>
                                <span>N</span>
                            </div>
                        </div>
                        <div className="video-loader-char">
                            <span style={{opacity: 0}}>G</span>
                            <div className="v-ticker-1"
                                style={{animationDuration: "30s"}}
                            >
                                <span>G</span>
                                <span>G</span>
                                <span>G</span>
                                <span>G</span>
                                <span>G</span>
                                <span>G</span>
                                <span>G</span>
                            </div>
                            <div className="v-ticker-2" style={{animationDuration: "30s"}}>
                                <span>G</span>
                                <span>G</span>
                                <span>G</span>
                                <span>G</span>
                                <span>G</span>
                                <span>G</span>
                                <span>G</span>
                            </div>
                        </div>
                    </div>
                </motion.div>
            }
            </AnimatePresence>
            <div className="proj_thumbnail__wrapper"
                onClick={handleVideoClick}
                ref={thumbnailWrapperRef}
            >
                <ProgressiveBackgroundImg
                    placeholderSrc={data.thumbnails.placeholder}
                    src={dynamicSrc}
                    idx={projectNumber}
                    forceFullWidth={true}
                    animateIn={false}
                    startFullRes={true}
                />
                {data.main}
            </div>
            <Vimeo 
                video={videoLink}
                className="project-video"
                height= {"100%"}
                controls={false}
                volume={volume}
                paused={!videoPlayingControl}
                onPlaying={handlePlayingStart}
                onPause={handlePlayingStopped}
                onSeeked={handleSeekingEnd}
                onError={handlePlayingStopped}
                onEnd={handleVideoEnd}
                onTimeUpdate={handleTimeUpdate}
                loop={false}
                dnt={true}
                onReady={player => {playerRef.current = player}}
                onClick={handleVideoClick}
            />
        </motion.div>
    )
}

export default ProjectVideo;